import {Button, Group, Text} from '@mantine/core';
import React, {useCallback} from 'react';

import ModalFooter from 'ui-library/modals/ModalFooter';
import {RunModalProps} from 'ui-library/runModal';

export interface FlowchartConfirmExitWithUnsavedChangesModalProps extends RunModalProps<boolean> {
  nodeName: string;
}

/**
 * Content component for the "Discard unsaved edits?" confirmation modal.
 *
 * Rendered inside a Mantine Modal via `runModal`. Resolves with `true` when the
 * user chooses to discard, or `undefined` when the user keeps editing (including
 * Escape and the close button).
 *
 * Escape handling: The Mantine container's built-in closeOnEscape is disabled
 * because this modal is opened in response to an Escape press — OS key-repeat
 * would immediately dismiss it. Instead we handle Escape ourselves, ignoring
 * repeat events.
 */
export default function FlowchartConfirmExitWithUnsavedChangesModal(
  props: FlowchartConfirmExitWithUnsavedChangesModalProps
): JSX.Element {
  const {nodeName, close} = props;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!event.repeat) {
        close();
      }
    },
    [close]
  );

  return (
    <div onKeyDown={handleKeyDown}>
      <Text size="sm">
        If you continue, you will lose your unsaved edits to <strong>{nodeName}</strong>.
      </Text>
      <ModalFooter>
        <Group position="right">
          <Button variant="tertiary" onClick={() => close()}>
            Keep editing
          </Button>
          <Button variant="warn" onClick={() => close(true)} data-autofocus={true}>
            Discard edits
          </Button>
        </Group>
      </ModalFooter>
    </div>
  );
}
